import { AI_MONTHLY_LIMIT, CREDIT_PACKS, MESSAGE_POINTS, PLAN_MODULES, PLAN_PRICES, PLAN_TEACHER_LIMIT, TRIAL_DAYS, YEARLY_PAID_MONTHS } from '@selfon/domain/browser';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS } from '../business';
import { MODULE_LABEL, PLAN_LABEL, won } from '../format';

/** 로그인 없이 볼 수 있는 페이지(결제대행사 심사·센터 문의용) */
export type PublicKind = 'about' | 'pricing' | 'refund' | 'support';


export function Pricing() {
  return (
    <article className="legal stack">
      <h2>요금 안내</h2>
      <p className="muted small">모든 금액은 부가세 포함입니다. 연간 결제는 2개월이 무료입니다.</p>
      <div className="table-wrap">
        <table className="table">
          <caption className="sr-only">요금제</caption>
          <thead><tr><th scope="col">요금제</th><th scope="col" className="num">월 요금</th><th scope="col">포함 기능</th></tr></thead>
          <tbody>
            <tr><td>{PLAN_LABEL.TRIAL}</td><td className="num">{TRIAL_DAYS}일 무료</td><td>모든 기능. 카드 등록 없이 시작합니다.</td></tr>
            {(Object.keys(PLAN_PRICES) as Array<keyof typeof PLAN_PRICES>).map((p) => (
              <tr key={p}><td>{PLAN_LABEL[p]}</td><td className="num">{won(PLAN_PRICES[p])}<br /><span className="muted small">연 {won(PLAN_PRICES[p] * YEARLY_PAID_MONTHS)}</span></td><td>선생님 {PLAN_TEACHER_LIMIT[p]}명까지. {PLAN_MODULES[p].map((m) => MODULE_LABEL[m]).join(', ')}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>선생님이 16명 이상이거나 여러 지점을 운영하면 고객센터로 문의해 주세요. AI 기능은 센터당 한 달 {AI_MONTHLY_LIMIT}회까지 쓸 수 있습니다. 보호자는 무료입니다.</p>
      <h3>알림톡·문자 포인트</h3>
      <p>1포인트 = 1원(부가세 포함). 카드로 충전합니다. 알림톡 1건에 {MESSAGE_POINTS}포인트를 씁니다.</p>
      <ul>{CREDIT_PACKS.map((n) => <li key={n}>{n.toLocaleString('ko-KR')}P · {won(n)}</li>)}</ul>
      <p className="small muted">요금제 변경·해지와 환불은 <Link to="/refund">환불 정책</Link>을 따릅니다.</p>
    </article>
  );
}

/** 환불·취소 정책 초안. 공개 전에 법률 검토가 필요하다(전자상거래법·콘텐츠 이용자보호지침 기준으로 작성). */
export function Refund() {
  return (
    <article className="legal stack">
      <h2>환불·취소 정책</h2>
      <p className="muted small">시행일: 2026년 11월 1일 (초안 — 법률 검토 전)</p>
      <h3>1. 적용 대상</h3>
      <p>SelfOn 의 요금제(Basic·Pro), 학생 월 이용권, 알림톡·문자 포인트 결제에 적용합니다. 무료체험은 결제가 없어 환불 대상이 아닙니다.</p>
      <h3>2. 요금제</h3>
      <ul>
        <li>결제일로부터 7일 이내이고 결제 후 유료 기능을 쓰지 않았다면 전액 환불합니다.</li>
        <li>이용 중 해지하면 다음 결제일부터 청구하지 않으며, 이미 결제한 기간은 끝까지 이용할 수 있습니다.</li>
        <li>남은 기간의 환불을 요청하면 결제 금액에서 이용한 날수만큼(일할)과 결제 수수료를 뺀 금액을 환불합니다.</li>
        <li>요금제를 올리면 바로 적용하고 남은 기간의 차액을 결제합니다. 내리거나 결제 주기를 바꾸면 다음 결제일부터 적용합니다.</li>
        <li>연간 결제를 중도에 환불할 때는 이용한 기간을 월 요금 기준으로 계산합니다.</li>
      </ul>
      <h3>2-1. 학생 월 이용권</h3>
      <ul>
        <li>결제일로부터 7일 이내이고 이용권을 쓰지 않았다면 결제한 보호자의 요청으로 전액 환불합니다.</li>
        <li>전액 환불하면 이용 기간에서 한 달을 되돌립니다.</li>
        <li>법정대리인의 동의가 없는 미성년자의 결제는 관계 법령에 따라 취소할 수 있습니다.</li>
      </ul>
      <h3>3. 알림톡·문자 포인트</h3>
      <ul>
        <li>충전일로부터 7일 이내이고 포인트를 쓰지 않았다면 전액 환불(결제 취소)합니다.</li>
        <li>일부 사용했다면 남은 유료 포인트를 결제 금액 비율로 환불합니다. 무상으로 받은 포인트는 환불하지 않습니다.</li>
        <li>포인트는 충전일로부터 5년 동안 쓸 수 있습니다.</li>
      </ul>
      <h3>4. 회사 사정으로 서비스를 제공하지 못하는 경우</h3>
      <p>서비스를 종료하거나 회사 책임으로 서비스를 계속 제공하지 못하면, 남은 기간의 요금과 남은 유료 포인트를 전액 환불합니다. 잘못 청구된 금액은 전액 돌려드립니다.</p>
      <h3>5. 신청 방법과 기간</h3>
      <ul>
        <li>원장님 계정에서 [공지·문의] 1:1 문의 또는 고객센터 <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> 로 신청합니다.</li>
        <li>확인 후 3영업일 안에 원래 결제 수단으로 취소·환불합니다. 카드사에 따라 반영까지 3~7영업일이 더 걸릴 수 있습니다.</li>
      </ul>
    </article>
  );
}

const FAQ: Array<{ topic: string; q: string; a: React.ReactNode }> = [
  { topic: '로그인', q: '비밀번호를 잊었어요', a: <>로그인 화면의 <strong>비밀번호 찾기</strong>에서 가입한 이메일로 새 비밀번호를 정할 수 있습니다.</> },
  { topic: '로그인', q: '아이디가 기억나지 않아요', a: <>아이디는 가입할 때 쓴 이메일입니다. 어떤 이메일인지 모르겠다면 소속 센터에 확인하거나 아래 고객센터로 문의해 주세요.</> },
  { topic: '로그인', q: '쓰던 중에 로그아웃됐어요', a: <>개인정보 보호를 위해 30분 동안 사용하지 않으면 자동으로 로그아웃됩니다. 다시 로그인하면 됩니다.</> },
  { topic: '센터 연결', q: '선생님 가입 코드는 어디서 받나요', a: <>소속 센터의 원장님께 요청해 주세요. 코드를 넣으면 그 센터로 가입됩니다.</> },
  { topic: '센터 연결', q: '보호자 연결 코드는 어디서 받나요', a: <>아이가 다니는 센터에서 8자리 코드를 받아 <strong>우리 아이</strong> 화면의 센터 연결에 입력합니다.</> },
  { topic: '결제·개인정보', q: '결제·환불은 어떻게 하나요', a: <><Link to="/refund">환불 정책</Link>을 확인해 주세요. 원장님은 로그인 후 공지·문의의 1:1 문의로도 신청할 수 있습니다.</> },
  { topic: '결제·개인정보', q: '내 개인정보를 보거나 지우고 싶어요', a: <><Link to="/privacy">개인정보처리방침</Link>에서 방법을 확인할 수 있습니다. 센터가 입력한 아이 정보는 소속 센터에도 요청해 주세요.</> },
];

/** 고객센터 안내. 로그인 전에도 열려야 한다(로그인이 안 될 때 가장 많이 찾는다). */
export function Support() {
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const copy = async () => {
    try { await navigator.clipboard.writeText(BUSINESS.email); setCopied('ok'); }
    catch { setCopied('fail'); }
  };
  const mailto = `mailto:${BUSINESS.email}?subject=${encodeURIComponent('[SelfOn 문의]')}`;
  return (
    <article className="support stack">
      <h2>고객센터</h2>
      <p className="muted">로그인이 안 되어도 이 화면에서 도움을 받을 수 있습니다.</p>

      <section className="support-contact" aria-labelledby="support-mail">
        <h3 id="support-mail">이메일 문의</h3>
        <p className="support-address">{BUSINESS.email}</p>
        <div className="support-actions">
          <button type="button" className="btn btn-primary" onClick={copy}>주소 복사</button>
          <a className="btn btn-outline" href={mailto}>메일 앱 열기</a>
        </div>
        <p className="small muted" role="status">
          {copied === 'ok' ? '주소를 복사했습니다. 쓰시는 메일에 붙여 넣어 보내 주세요.' : copied === 'fail' ? '복사하지 못했습니다. 위 주소를 직접 선택해 복사해 주세요.' : '메일 앱이 열리지 않으면 주소를 복사해 쓰시는 메일에서 보내 주세요.'}
        </p>
        {BUSINESS.phone && <p>전화 <a href={`tel:${BUSINESS.phone.replace(/[^0-9+]/g, '')}`}>{BUSINESS.phone}</a></p>}
        <ul className="small">
          {BUSINESS.hours && <li>운영 시간: {BUSINESS.hours}</li>}
          <li>영업일 기준 3일 안에 답변드립니다.</li>
          <li>센터 이름, 역할(원장님·선생님·보호자·학생), 겪고 있는 문제를 적어 주시면 빨리 도와드릴 수 있습니다.</li>
          <li><strong>비밀번호, 주민등록번호, 카드번호, 아이의 건강·장애 정보는 메일에 적지 말아 주세요.</strong></li>
        </ul>
      </section>

      <section aria-labelledby="support-faq">
        <h3 id="support-faq">자주 묻는 질문</h3>
        {[...new Set(FAQ.map((f) => f.topic))].map((topic) => (
          <div key={topic} className="support-faq">
            <h4>{topic}</h4>
            {FAQ.filter((f) => f.topic === topic).map((f) => <details key={f.q}><summary>{f.q}</summary><p>{f.a}</p></details>)}
          </div>
        ))}
      </section>
    </article>
  );
}

/** 사업자 정보 표시. 아직 등록하지 않은 칸은 나오지 않는다. */
export function BusinessInfo() {
  const rows = [['상호', BUSINESS.name], ['대표', BUSINESS.ceo], ['사업자등록번호', BUSINESS.regNo], ['통신판매업 신고', BUSINESS.mailOrderNo], ['주소', BUSINESS.address]].filter(([, v]) => v);
  return (
    <p className="business-info small muted">
      {rows.length > 0 && <>{rows.map(([k, v]) => `${k} ${v}`).join(' · ')}<br /></>}
      <Link to="/support">고객센터</Link> {BUSINESS.phone ? `${BUSINESS.phone} · ` : ''}<a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
    </p>
  );
}

const PUBLIC_PATHS = ['/about', '/pricing', '/refund', '/support', '/terms', '/privacy'] as const;
export type PublicPath = (typeof PUBLIC_PATHS)[number];
export const isPublicPath = (p: string): p is PublicPath => (PUBLIC_PATHS as readonly string[]).includes(p);

/** 로그인하지 않은 방문자가 공개 페이지 주소로 들어왔을 때의 화면 틀. */
export function PublicShell({ path, children }: { path: PublicPath; children: React.ReactNode }) {
  return (
    <div className="public-page">
      <header className="public-head">
        <Link to="/" className="public-brand" aria-label="SelfOn 첫 화면">SelfOn</Link>
        <nav aria-label="안내 페이지">
          {([['/about', '서비스 소개'], ['/pricing', '요금 안내'], ['/refund', '환불 정책'], ['/support', '고객센터'], ['/terms', '이용약관'], ['/privacy', '개인정보처리방침']] as const).map(([to, label]) => (
            <Link key={to} to={to} aria-current={to === path ? 'page' : undefined}>{label}</Link>
          ))}
          <Link to="/" className="btn btn-primary btn-sm">로그인</Link>
        </nav>
      </header>
      <main id="main" tabIndex={-1} className="public-main">{children}</main>
      <footer className="public-foot"><BusinessInfo /><span className="small muted">© 2026 SelfOn</span></footer>
    </div>
  );
}
